import { useAsObservableSource, useLocalStore } from 'mobx-react-lite';

import { useTranslation } from '@gem/modules/i18n';
import { IPopoverMenuItem } from '@gem/ui-kit';

import Tasks from 'APP/Tasks';
import { unbanUsersForGroup } from 'APP/model/group/groupModel';
import Entities from 'STORE';
import { Group } from 'STORE/Groups/Group';
import type { User } from 'STORE/Users/User/User';

export interface IBlackListMenuPresenterParams {
  user: User | null;
  toggleMenu(ref: HTMLElement | null, user: User): void;
}

interface IBlackListMenuPresenter {
  group: Group | null;
  isOpened: boolean;
  menuItems: IPopoverMenuItem[];
  closeMenu(): void;
  unbanUser(): Promise<void>;
}

export const useBlackListMenuPresenter = ({
  toggleMenu,
  user,
}: IBlackListMenuPresenterParams): IBlackListMenuPresenter => {
  const { t } = useTranslation();
  const source = useAsObservableSource({ user });

  const presenter = useLocalStore<IBlackListMenuPresenter>(() => ({
    get group(): Group | null {
      return Entities.GroupsStore.activeGroup;
    },

    closeMenu(): void {
      toggleMenu(null, source.user!);
    },

    get isOpened(): boolean {
      return !!source.user;
    },

    get menuItems(): IPopoverMenuItem[] {
      return [
        {
          key: 'unban',
          title: t('unban_user'),
          onClick: () => presenter.unbanUser(),
        },
      ];
    },

    async unbanUser(): Promise<void> {
      const user = source.user;

      if (!presenter.group || !user) {
        return;
      }

      const canUnban = await Tasks.app.showConfirm({
        text: t('unban_user_alert', { 0: user.displayName }),
      });

      if (canUnban) {
        try {
          await unbanUsersForGroup({
            groupId: presenter.group!.id,
            usersToUnban: [user.id],
          });
        } catch {
          Entities.toast.show(t('something_went_wrong'));
        }
      }

      presenter.closeMenu();
    },
  }));

  return presenter;
};
